// Multi-file upload queue for the free-supplier template dropzone
// (CommsTemplateDropzone / SupplierTemplateUploadFlow).
//
// POST /supplier/uploads accepts exactly ONE file per request and the
// backend runs a 2-slot parse semaphore, so N dropped/picked files are
// uploaded SEQUENTIALLY — never in parallel; a 3rd concurrent parse (from
// this tab or another) gets rejected with PARSER_BUSY rather than queued
// server-side. This module is the pure, framework-free queue model — item
// shape, the reducer, and the selectors the component's processing effect
// and render use — kept dependency-free (no React) so the sequencing
// invariants (only one item uploads at a time; a failed file never blocks
// the rest of the batch) are unit-testable without mounting anything.

export const UploadQueueItemStatus = Object.freeze({
  Queued: 'queued',
  Uploading: 'uploading',
  Done: 'done',
  Failed: 'failed',
});

export const MAX_SUPPLIER_UPLOAD_FILE_BYTES = 14 * 1024 * 1024;

// The client-side pre-checks the dropzone has always run before a file ever
// reaches the network (.xlsx extension, 14MB ceiling). Extracted here so
// every queued file is checked independently — one bad file rejects into
// `failed` without ever blocking (or being blocked by) the rest of the batch.
export function precheckSupplierUploadFile(file) {
  if (!file || !file.name || !file.name.match(/\.xlsx$/i)) {
    return 'Only .xlsx workbooks are accepted.';
  }
  if (file.size > MAX_SUPPLIER_UPLOAD_FILE_BYTES) {
    return 'The workbook exceeds the 14 MB limit.';
  }
  return null;
}

let queueItemSeq = 0;
const nextQueueItemId = () => `supplier-upload-queue-${Date.now()}-${queueItemSeq++}`;

// One row in the queue.
// - `file`: the raw browser File.
// - `result`: the 201 detail response ({version, upload, diagram, drafts})
//   once `done`.
// - `serverError`: {code, message, issues?} from the rejection envelope once
//   the SERVER rejected the file (400/422/429) — paired with
//   supplierUploadRejectionCopy for the human-readable title/body.
// - `reportingPeriod`: {from, to} entered after a REPORTING_PERIOD_REQUIRED
//   rejection; sent with the file on the retry that entering it triggers.
// - `clientError`: a plain string when the PRE-CHECK rejected the file
//   before any request was made — never reaches the server at all.
export function createUploadQueueItem(file) {
  const clientError = precheckSupplierUploadFile(file);
  return {
    id: nextQueueItemId(),
    file,
    status: clientError ? UploadQueueItemStatus.Failed : UploadQueueItemStatus.Queued,
    clientError,
    serverError: null,
    result: null,
    reportingPeriod: null,
    dismissed: false,
  };
}

// The one server rejection the supplier resolves right here, on the file's own
// row: entering the reporting period re-sends the file. (Kept as a literal so
// this module stays dependency-free.)
const PERIOD_REQUIRED_CODE = 'REPORTING_PERIOD_REQUIRED';

// A failed file that is still waiting on the supplier: it needs a reporting
// period and they have neither entered one nor chosen to go on without it.
export const isUploadQueueItemAwaitingPeriod = (item) =>
  item.status === UploadQueueItemStatus.Failed &&
  item.serverError?.code === PERIOD_REQUIRED_CODE &&
  !item.dismissed;

export function uploadQueueReducer(items, action) {
  switch (action.type) {
    case 'ADD_FILES':
      return [...items, ...action.files.map(createUploadQueueItem)];
    case 'START':
      return items.map((it) =>
        it.id === action.id
          ? { ...it, status: UploadQueueItemStatus.Uploading, serverError: null }
          : it,
      );
    case 'SUCCESS':
      return items.map((it) =>
        it.id === action.id
          ? { ...it, status: UploadQueueItemStatus.Done, result: action.result }
          : it,
      );
    case 'FAILURE':
      return items.map((it) =>
        it.id === action.id
          ? {
              ...it,
              status: UploadQueueItemStatus.Failed,
              serverError: action.error,
              result: null,
            }
          : it,
      );
    case 'RETRY':
      return items.map((it) => {
        if (it.id !== action.id) return it;
        // Re-run the pre-check rather than trusting the file is still
        // valid: a client-rejected file (bad extension/size) can never
        // succeed and stays `failed` without a wasted request; a
        // server-rejected file goes back to `queued` so the sequencer picks
        // it up again (this covers PARSER_BUSY/PARSER_TIMEOUT retries and
        // any other retryable server rejection uniformly).
        const clientError = precheckSupplierUploadFile(it.file);
        return {
          ...it,
          status: clientError ? UploadQueueItemStatus.Failed : UploadQueueItemStatus.Queued,
          clientError,
          serverError: null,
          result: null,
          dismissed: false,
          // A plain retry keeps whatever period was already entered.
          reportingPeriod: action.reportingPeriod ?? it.reportingPeriod ?? null,
        };
      });
    case 'DISMISS':
      // "Continue without this file": the row stays failed, but no longer
      // holds the batch back.
      return items.map((it) => (it.id === action.id ? { ...it, dismissed: true } : it));
    case 'RESET':
      return [];
    default:
      return items;
  }
}

// The single item the sequencer should upload next — null while another
// item is mid-upload (never parallel) or when nothing is queued. This is
// the crux of the sequentiality guarantee: as long as one item is
// `uploading`, every other `queued` item is invisible to the sequencer.
export function nextQueueItemToProcess(items) {
  if (items.some((it) => it.status === UploadQueueItemStatus.Uploading)) return null;
  return items.find((it) => it.status === UploadQueueItemStatus.Queued) || null;
}

export const isUploadQueueSettled = (items) =>
  items.length > 0 &&
  items.every(
    (it) => it.status === UploadQueueItemStatus.Done || it.status === UploadQueueItemStatus.Failed,
  );

export const uploadQueueHasSuccess = (items) =>
  items.some((it) => it.status === UploadQueueItemStatus.Done);

// True once the whole batch is finalized AND worth handing off to the
// caller as a result — i.e. it produced at least one draft. A batch that
// finishes with everything failed stays on-screen instead (every row keeps
// its own Retry) rather than forcing a transition to an empty "success" view.
//
// One more condition: no file is still waiting for its reporting period. The
// caller replaces the queue with a summary on completion, and the prompt that
// asks for the period lives on the queue row — handing off early would take
// the question away before it could be answered.
export const isUploadQueueReadyToComplete = (items) =>
  isUploadQueueSettled(items) &&
  uploadQueueHasSuccess(items) &&
  !items.some(isUploadQueueItemAwaitingPeriod);

export const uploadQueueDraftCount = (items) =>
  items.reduce((sum, it) => sum + (it.result?.drafts?.length || 0), 0);

export const uploadQueueSuccessCount = (items) =>
  items.filter((it) => it.status === UploadQueueItemStatus.Done).length;

export const uploadQueueFailureCount = (items) =>
  items.filter((it) => it.status === UploadQueueItemStatus.Failed).length;
